import { Theme, CSSObject } from '@mui/material';

/**
 * BoxShadow style used by the Focus Ring
 *  boxShadow: theme => getFocusRingBoxShadow(theme);
 *
 * @param theme - @mui theme
 * @param inset - put box shadow inside
 */
const getFocusRingBoxShadow = (theme: Theme, inset?: boolean) =>
  `${inset ? 'inset ' : ''}0 0 0 2px ${theme.palette.info.main}, ${inset ? 'inset ' : ''}0 0 0 4px ${theme.palette.background.primary}`;

/**
 * Style for interactive elements that should have blue & white ring around them once focused
 *
 * @param theme - @mui theme
 * @param smooth - controls smooth appearance of focusRing
 * @param border - component border to correctly place focusRing, can be used as string to directly manipulate inset
 * @param focusSelector - focus visible selector
 */
const getFocusRingStyle = (
  theme: Theme,
  smooth: boolean = true,
  border: number | string = 0,
  focusSelector = '.Mui-focusVisible',
): CSSObject => ({
  // pseudo-element outline, smooth generates :before element even without focus for the smooth appearance
  [smooth ? '&:before' : `&${focusSelector}:before`]: {
    content: '""',
    display: 'block',
    position: 'absolute',
    borderRadius: 'inherit',
    // beware, inset must be boosted with the border on the parent element
    inset: typeof border === 'string' ? border : `-${border + 2}px`,
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    pointerEvents: 'none',
    // hidden by default
    boxShadow: smooth ? 'none' : getFocusRingBoxShadow(theme),
    // smooth appearance
    ...(smooth && {
      transition: 'box-shadow 0.3s ease-out',
    }),
  },
  ...(smooth && {
    // show with @mui focus class
    [`&${focusSelector}:before`]: {
      boxShadow: getFocusRingBoxShadow(theme),
    },
  }),
});

/**
 * Any element applying border have to adjust position of ring to keep 2px space around element
 *
 * @param border - component border to correctly place focusRing, can be used as string to directly manipulate inset
 * @param smooth - controls smooth appearance of focusRing
 * @param focusSelector - focus visible selector
 */
const updateFocusRingStyle = (
  border: number | string = 0,
  smooth: boolean = true,
  focusSelector = '.Mui-focusVisible',
): CSSObject => ({
  [smooth ? '&&:before' : `&${focusSelector}:before`]: {
    inset: typeof border === 'string' ? border : `-${border + 2}px`,
  },
});

export { getFocusRingBoxShadow, getFocusRingStyle, updateFocusRingStyle };
